import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { toast } from "react-toastify";
import { beaconPublicAnswers, completePublicSurvey, getPublicSurvey, savePublicAnswers } from "../../../data/loca/satisfaction";
import "../../../assets/css/satisfaction.css";

// Page publique du questionnaire de satisfaction (/avis/:token).
// Objectif : rapide a remplir sur mobile, et rien n'est perdu si le locataire
// quitte avant la fin : chaque reponse est enregistree en arriere-plan
// (debounce), et les reponses en attente partent via sendBeacon a la fermeture.

export async function loader({ params }) {
    try {
        const survey = await getPublicSurvey(params.token);
        return { survey, token: params.token };
    } catch (error) {
        return { survey: null, token: params.token };
    }
}

const SAVE_DELAY_MS = { Text: 900, default: 200 };
const RATING_HINTS = { 1: "Pas du tout", 5: "Parfait" };

function isAnswered(value) {
    return value != null && (value.valueNumber != null || (value.valueText ?? "").trim() !== "");
}

function formatDate(value) {
    return new Date(value).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function PublicSatisfaction() {
    const { survey, token } = useLoaderData();

    const [answers, setAnswers] = useState(() => {
        const initial = {};
        (survey?.questions ?? []).forEach((question) => {
            initial[question.publicId] = { valueNumber: question.valueNumber ?? null, valueText: question.valueText ?? null };
        });
        return initial;
    });
    const [completed, setCompleted] = useState(Boolean(survey?.completed));
    const [editing, setEditing] = useState(!survey?.completed);
    const [saveStatus, setSaveStatus] = useState("idle");
    const [completing, setCompleting] = useState(false);
    const [highlightMissing, setHighlightMissing] = useState(false);

    // Reponses modifiees non encore envoyees au serveur (par question).
    const pendingRef = useRef(new Map());
    const timerRef = useRef(null);
    const flushingRef = useRef(false);

    const questions = useMemo(() => survey?.questions ?? [], [survey]);
    const generalQuestions = useMemo(() => questions.filter((question) => question.section === "general"), [questions]);
    const toolSections = useMemo(() => {
        const sections = [];
        questions.filter((question) => question.section === "tool").forEach((question) => {
            let section = sections.find((item) => item.toolPublicId === question.toolPublicId);
            if (!section) {
                section = { toolPublicId: question.toolPublicId, toolName: question.toolName, questions: [] };
                sections.push(section);
            }
            section.questions.push(question);
        });
        return sections;
    }, [questions]);

    const answeredCount = useMemo(() => questions.filter((question) => isAnswered(answers[question.publicId])).length, [questions, answers]);
    const missingRequired = useMemo(
        () => questions.filter((question) => question.isRequired && !isAnswered(answers[question.publicId])).map((question) => question.publicId),
        [questions, answers]
    );
    const progress = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;

    const takePending = useCallback(() => {
        const list = Array.from(pendingRef.current.entries()).map(([questionPublicId, value]) => ({
            questionPublicId,
            valueNumber: value.valueNumber ?? null,
            valueText: value.valueText ?? null
        }));
        pendingRef.current.clear();
        return list;
    }, []);

    const flush = useCallback(async () => {
        if (flushingRef.current || pendingRef.current.size === 0) {
            return;
        }

        flushingRef.current = true;
        const batch = takePending();
        setSaveStatus("saving");
        try {
            await savePublicAnswers(token, batch);
            setSaveStatus("saved");
        } catch (error) {
            // On remet le lot en attente : il repartira a la prochaine modification ou dans 4 s.
            batch.forEach((item) => {
                if (!pendingRef.current.has(item.questionPublicId)) {
                    pendingRef.current.set(item.questionPublicId, { valueNumber: item.valueNumber, valueText: item.valueText });
                }
            });
            setSaveStatus("error");
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => flush(), 4000);
        } finally {
            flushingRef.current = false;
            if (pendingRef.current.size > 0 && !timerRef.current) {
                timerRef.current = setTimeout(() => {
                    timerRef.current = null;
                    flush();
                }, SAVE_DELAY_MS.default);
            }
        }
    }, [token, takePending]);

    const scheduleFlush = useCallback((delay) => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            flush();
        }, delay);
    }, [flush]);

    const setAnswer = useCallback((question, value) => {
        setAnswers((current) => ({ ...current, [question.publicId]: value }));
        pendingRef.current.set(question.publicId, value);
        setSaveStatus("pending");
        scheduleFlush(question.type === "Text" ? SAVE_DELAY_MS.Text : SAVE_DELAY_MS.default);
    }, [scheduleFlush]);

    // Fermeture / mise en arriere-plan de l'onglet : on envoie ce qui reste via sendBeacon.
    useEffect(() => {
        const sendPending = () => {
            if (pendingRef.current.size === 0) {
                return;
            }
            clearTimeout(timerRef.current);
            timerRef.current = null;
            const batch = takePending();
            if (!beaconPublicAnswers(token, batch)) {
                batch.forEach((item) => pendingRef.current.set(item.questionPublicId, { valueNumber: item.valueNumber, valueText: item.valueText }));
            }
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                sendPending();
            }
        };

        window.addEventListener("pagehide", sendPending);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            window.removeEventListener("pagehide", sendPending);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            clearTimeout(timerRef.current);
        };
    }, [token, takePending]);

    const complete = async () => {
        if (missingRequired.length > 0) {
            setHighlightMissing(true);
            const first = document.getElementById(`sat-q-${missingRequired[0]}`);
            first?.scrollIntoView({ behavior: "smooth", block: "center" });
            toast.warn(missingRequired.length === 1
                ? "Merci de répondre à la question obligatoire avant de terminer."
                : `Merci de répondre aux ${missingRequired.length} questions obligatoires avant de terminer.`);
            return;
        }

        setCompleting(true);
        clearTimeout(timerRef.current);
        timerRef.current = null;
        try {
            await completePublicSurvey(token, takePending());
            setCompleted(true);
            setEditing(false);
            setSaveStatus("saved");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error) {
            toast.error(error?.message || "Impossible d'enregistrer le questionnaire, merci de réessayer.");
        } finally {
            setCompleting(false);
        }
    };

    if (!survey) {
        return (
            <div className="sat-page">
                <div className="sat-error">
                    <h1>Questionnaire introuvable</h1>
                    <p>Ce lien n'est plus valide ou le questionnaire n'existe pas. Vous pouvez contacter directement votre loueur.</p>
                </div>
            </div>
        );
    }

    const overall = generalQuestions.find((question) => question.type === "Rating");
    const overallScore = overall ? answers[overall.publicId]?.valueNumber : null;

    const renderStatus = () => {
        switch (saveStatus) {
            case "saving":
            case "pending":
                return <span className="sat-progress-status is-saving">Enregistrement…</span>;
            case "saved":
                return <span className="sat-progress-status is-saved"><i className="pi pi-check" aria-hidden="true" /> Enregistré</span>;
            case "error":
                return <span className="sat-progress-status is-error">Hors ligne, nouvel essai…</span>;
            default:
                return <span className="sat-progress-status">{answeredCount}/{questions.length}</span>;
        }
    };

    const renderQuestion = (question) => {
        const value = answers[question.publicId] ?? { valueNumber: null, valueText: null };
        const answered = isAnswered(value);
        const missing = highlightMissing && question.isRequired && !answered;
        const className = `sat-question${answered ? " is-answered" : ""}${missing ? " is-missing" : ""}`;

        return (
            <div key={question.publicId} id={`sat-q-${question.publicId}`} className={className}>
                <p className="sat-question-label">
                    <span>{question.label}</span>
                    {question.isRequired && <span className="sat-required">Obligatoire</span>}
                </p>

                {question.type === "Rating" && (
                    <>
                        <div className="sat-rating" role="radiogroup" aria-label={question.label}>
                            {[1, 2, 3, 4, 5].map((score) => (
                                <button
                                    key={score}
                                    type="button"
                                    role="radio"
                                    aria-checked={value.valueNumber === score}
                                    aria-label={`${score} sur 5`}
                                    className={value.valueNumber != null && score <= value.valueNumber ? "is-on" : ""}
                                    onClick={() => setAnswer(question, { valueNumber: value.valueNumber === score ? null : score, valueText: null })}
                                >
                                    <i className={value.valueNumber != null && score <= value.valueNumber ? "pi pi-star-fill" : "pi pi-star"} aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                        <div className="sat-rating-hint">
                            <span>{RATING_HINTS[1]}</span>
                            <span>{RATING_HINTS[5]}</span>
                        </div>
                    </>
                )}

                {question.type === "YesNo" && (
                    <div className="sat-choices sat-choices-yesno" role="radiogroup" aria-label={question.label}>
                        {[{ label: "Oui", number: 1 }, { label: "Non", number: 0 }].map((option) => (
                            <button
                                key={option.number}
                                type="button"
                                role="radio"
                                aria-checked={value.valueNumber === option.number}
                                className={value.valueNumber === option.number ? "is-on" : ""}
                                onClick={() => setAnswer(question, { valueNumber: value.valueNumber === option.number ? null : option.number, valueText: null })}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                )}

                {question.type === "Choice" && (
                    <div className="sat-choices" role="radiogroup" aria-label={question.label}>
                        {(question.options ?? []).map((option, index) => (
                            <button
                                key={option}
                                type="button"
                                role="radio"
                                aria-checked={value.valueNumber === index}
                                className={value.valueNumber === index ? "is-on" : ""}
                                onClick={() => setAnswer(question, { valueNumber: value.valueNumber === index ? null : index, valueText: value.valueNumber === index ? null : option })}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                )}

                {question.type === "Text" && (
                    <InputTextarea
                        value={value.valueText ?? ""}
                        onChange={(event) => setAnswer(question, { valueNumber: null, valueText: event.target.value })}
                        autoResize
                        rows={3}
                        maxLength={2000}
                        placeholder={question.isRequired ? "Votre réponse" : "Votre réponse (facultatif)"}
                        aria-label={question.label}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="sat-page">
            <div className="sat-hero">
                {survey.picture && <img src={survey.picture} alt="" />}
                <div className="sat-hero-body">
                    <p className="sat-kicker">Votre avis compte</p>
                    <h1>{survey.rentalName}</h1>
                    <p>
                        Location du {formatDate(survey.startDate)} au {formatDate(survey.endDate)}
                        {survey.ownerName ? ` · ${survey.ownerName}` : ""}
                    </p>
                </div>
            </div>

            {completed && !editing ? (
                <div className="sat-done">
                    <div className="sat-done-icon"><i className="pi pi-check" aria-hidden="true" /></div>
                    <h2>Merci {survey.clientFirstname} !</h2>
                    {overallScore != null && (
                        <div className="sat-stars" aria-label={`Note globale ${overallScore} sur 5`}>
                            {"★".repeat(overallScore)}{"☆".repeat(5 - overallScore)}
                        </div>
                    )}
                    <p>Vos réponses ont bien été transmises à {survey.ownerName || "votre loueur"}. Elles l'aideront à améliorer ses prochaines locations.</p>
                    <Button label="Modifier mes réponses" icon="pi pi-pencil" text onClick={() => setEditing(true)} />
                </div>
            ) : (
                <>
                    <p className="sat-intro">
                        Bonjour {survey.clientFirstname}, quelques questions rapides sur votre location.
                        Vos réponses sont enregistrées au fur et à mesure : vous pouvez quitter et revenir à tout moment.
                    </p>

                    <div className="sat-progress" aria-live="polite">
                        <div className="sat-progress-bar" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                            <span style={{ width: `${progress}%` }} />
                        </div>
                        {renderStatus()}
                    </div>

                    {generalQuestions.length > 0 && (
                        <section className="sat-section">
                            <h2>Votre location</h2>
                            {generalQuestions.map(renderQuestion)}
                        </section>
                    )}

                    {toolSections.map((section) => (
                        <section key={section.toolPublicId} className="sat-section">
                            <h2>
                                {section.toolName}
                                <small>Quelques questions sur ce matériel</small>
                            </h2>
                            {section.questions.map(renderQuestion)}
                        </section>
                    ))}

                    <div className="sat-footer">
                        <Button
                            label={completed ? "Enregistrer mes modifications" : "Terminer"}
                            icon="pi pi-send"
                            loading={completing}
                            disabled={completing}
                            onClick={complete}
                        />
                        <p className="sat-footer-note">
                            {completed
                                ? "Vos réponses restent modifiables."
                                : "Une minute suffit. Rien n'est perdu si vous quittez avant la fin."}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}
